'use client';

import { useCallback, useId, useRef, useState } from 'react';
import Image from 'next/image';
import clsx from 'clsx';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname } from '@/i18n/navigation';
import { routing } from '@/i18n/routing';
import { Button, Span } from '@/components/ui';
import { ChevronDownIcon, LocaleFlagPaths } from '@/assets/icons';
import type { AppLocale } from '@/i18n/localeConfig';
import AppLink from '@/components/i18n/AppLink';
import { usePopoverDismiss } from '@/hooks/usePopoverDismiss';

export type LocaleDropdownProps = {
  className?: string;
};

function localeLinks(menu: HTMLElement | null): HTMLElement[] {
  if (!menu) return [];
  return Array.from(menu.querySelectorAll<HTMLElement>('a.navbar-locale-option'));
}

function focusLocaleLink(menu: HTMLElement | null, which: 'first' | 'last') {
  const links = localeLinks(menu);
  if (!links.length) return;
  (which === 'first' ? links[0] : links[links.length - 1])?.focus();
}

function focusAdjacentLocaleLink(menu: HTMLElement | null, delta: 1 | -1) {
  const links = localeLinks(menu);
  if (!links.length) return;
  const active = document.activeElement as HTMLElement | null;
  let i = links.indexOf(active!);
  if (i < 0) i = delta === 1 ? -1 : 0;
  else i = (i + delta + links.length) % links.length;
  links[i]?.focus();
}

/**
 * Compact language selector for the navbar: disclosure button + list of locale links
 * (preserves pathname via `AppLink` / `next-intl`). Closes on outside click / Escape;
 * Arrow keys move between options when the menu is open.
 */
export default function LocaleDropdown({ className }: LocaleDropdownProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const pathname = usePathname();
  const active = useLocale();
  const t = useTranslations('localeSwitcher');
  const menuId = useId();

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  usePopoverDismiss({ open, onClose: close, rootRef });

  const labelFor = (loc: string) => {
    try {
      return t(loc as AppLocale);
    } catch {
      return loc;
    }
  };

  const flagSrc = (loc: string) => LocaleFlagPaths[loc as AppLocale] ?? null;

  const activeFlag = flagSrc(active);

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Escape') {
      if (!open) return;
      e.preventDefault();
      close();
      return;
    }

    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      setOpen(true);
      queueMicrotask(() => focusLocaleLink(menuRef.current, e.key === 'ArrowDown' ? 'first' : 'last'));
      return;
    }

    if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      focusAdjacentLocaleLink(menuRef.current, e.key === 'ArrowDown' ? 1 : -1);
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        focusAdjacentLocaleLink(e.currentTarget, 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusAdjacentLocaleLink(e.currentTarget, -1);
        break;
      case 'Home':
        e.preventDefault();
        focusLocaleLink(e.currentTarget, 'first');
        break;
      case 'End':
        e.preventDefault();
        focusLocaleLink(e.currentTarget, 'last');
        break;
      default:
        break;
    }
  };

  return (
    <div ref={rootRef} className={clsx('navbar-locale-dropdown', className)} data-widget="LocaleDropdown">
      <Button
        ref={triggerRef}
        type="button"
        className="navbar-locale-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t('label')}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        {activeFlag ? (
          <Image
            src={activeFlag}
            alt={labelFor(active)}
            title={labelFor(active)}
            width={16}
            height={16}
            className="navbar-locale-flag pointer-events-none"
          />
        ) : null}
        <Span className="navbar-locale-trigger-text">{labelFor(active)}</Span>
        <ChevronDownIcon
          width={10}
          height={10}
          strokeWidth={2}
          className={clsx('navbar-locale-chevron', open && 'rotate-180')}
          aria-hidden
        />
      </Button>
      {open ? (
        <ul
          id={menuId}
          ref={menuRef}
          className="navbar-locale-menu"
          aria-label={t('label')}
          onKeyDown={onListKeyDown}
        >
          {routing.locales.map((loc) => {
            const optionFlag = flagSrc(loc);
            return (
              <li key={loc} className="navbar-locale-item">
                <AppLink
                  href={pathname}
                  locale={loc}
                  hrefLang={loc}
                  className={clsx('navbar-locale-option', loc === active && 'is-active')}
                  onClick={() => setOpen(false)}
                >
                  {optionFlag ? (
                    <Image
                      src={optionFlag}
                      alt=""
                      title={labelFor(loc)}
                      width={12}
                      height={12}
                      className="navbar-locale-flag pointer-events-none"
                      aria-hidden
                    />
                  ) : null}
                  <Span className="navbar-locale-option-label">{labelFor(loc)}</Span>
                </AppLink>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
